// Components/Tabs/MachineDetailsTab.jsx

import React, { useCallback } from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import SliderFormDropDownExtra from "../EditMachineDropDown/EditMachineDropDown";
import useAdminToast from "../useAdminToast";
import Backend from "../../../utils/api.js";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * MachineDetailsTab Component
 * 
 * This component displays and allows editing of machine details. It fetches machine data
 * from the backend and provides a form for updating machine information.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {number} props.machineId - The unique identifier of the machine.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function MachineDetailsTab({ user, machineId, labId, pushTab, revert }) {

  const toast = useAdminToast();
  useDocumentTitle("Admin: Tool instance details – Pinpoint");
  const loadInstance = useCallback(() => Backend.getMachineById(user.UID, machineId), [user.UID, machineId]);
  const loadTools = useCallback(() => Backend.getMachineTypes(user.UID, 1000), [user.UID]);
  const loadCategories = useCallback(() => Backend.getToolCategoriesByLab(user.UID, labId, 1000), [user.UID, labId]);

  return (
    <Slider
      name="Tool instance details"
      navColor="rgb(200, 170, 190)"
      slide={true}
      index={0} // Ensure correct index based on stack
      onClick={() => revert(1)} // Revert one step when navigation bar is clicked
    >
      {/* SliderFormDropDownExtra displays the form with dropdowns for machine type and tool categories */}
      <SliderFormDropDownExtra
        title="Tool"
        fields={[
          {
            key: "name",
            placeholder: "Tool instance name",
            validate: /^[A-Za-z0-9]+(?:\s[A-Za-z0-9\.)(*&^%$#@!)]+)*$/,
            errorText:
              "Must be at least one word and have no leading or trailing spaces.",
          },
        ]}
        dataFunctionAuto={loadInstance} // Fetch current machine data
        dataFunctionMachineTypes={loadTools} // Fetch machine types
        dataFunctionToolCategories={loadCategories} // Fetch tool categories
        onCancel={() => revert(1)}
        onSubmit={async (values) => {
          const response = await Backend.editMachine(user.UID, machineId, {
            machineName: values.name, machineType: values.machineType,
            categoryID: values.categoryID, lab: labId,
          });
          if (response.status !== 201 && response.status !== 200) throw new Error("save_failed");
          toast({ title: "Tool instance saved" });
        }}
        onDelete={async () => {
          const response = await Backend.editMachine(user.UID, machineId, { deleted: true, lab: labId });
          if (response.status !== 201 && response.status !== 200) throw new Error("delete_failed");
          toast({ title: "Tool instance deleted" });
          revert(1);
        }}
      />
    </Slider>
  );
}

// PropTypes for type-checking component props
MachineDetailsTab.propTypes = {
  user: PropTypes.object.isRequired,
  machineId: PropTypes.number.isRequired, // Ensure machineId is a number
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default MachineDetailsTab;
